import { getId } from '@/assets/js/utils';
import { xhr } from '@/xhr/endpoints';

//
// state
const state = {
  currentTeamMemberID: '',
  generalInstructions: {},
  currentTeam: null,
  debateTeams: {},
  isTeamModeActive: false,
};

//
// getters
const getters = {
  teamID: state => {
    return getId(state.debateStatus?.team);
  },
  teamMembers: state => {
    return state.documentData?.team?.members?.filter(tm => tm.active) || [];
  },
  teamMemberData: state => {
    return state.currentTeam?.members?.find(tm => getId(tm.user) === getId(state.userData));
  },
  globalTeamMembers: state => state.userData.members,
  nonTeamMembers: (state, getters) => {
    let userIdArr = getters.teamMembers.map(tm => (tm.user ? getId(tm.user._id) : getId(tm)));
    return getters.globalTeamMembers.filter(tm => !userIdArr.includes(tm));
  },

  /** Validate if the current team has members */
  currentTeamHasMembers: state => {
    return state.currentTeam?.members?.some(x => x.status === 'Active');
  },

  /** Validate if not all team members have submitted their annotations */
  teamMemberSubmissionsPending: state => {
    return state.currentTeam?.members?.some(x => !x.submitted);
  },

  opposingRounds: state =>
    [state.debateStatus.roundZero, ...state.debateStatus.rounds].find(
      r => r.team._id !== state.currentTeam?._id
    ),

  opposingTeam: state => {
    const opposingRound = [state.debateStatus.roundZero, ...state.debateStatus.rounds].find(
      r => r.team._id !== state.currentTeam?._id
    );
    return {
      _id: opposingRound?.team._id,
      owner: { _id: opposingRound?.author._id },
    };
  },

  cohortTeam: state => {
    const cohortRound = state.debateStatus.rounds[0].team._id;
    return {
      _id: cohortRound,
    };
  },
};

//
// mutations
const mutations = {
  setCurrentTeam(state, team) {
    state.currentTeam = team;
  },
  setTeams(state, team) {
    state.debateTeams[team._id] = team;
  },
  setTeamMode(state, status) {
    state.isTeamModeActive = status;
  },
  setCurrentTeamMemberID(state, teamMemberID) {
    state.currentTeamMemberID = teamMemberID;
  },
  setGeneralInstructions(state, generalInstructions) {
    state.generalInstructions = generalInstructions;
    //
    // if no instructions set default values
    // @Validation:
    //      has general instructions
    if (!state.generalInstructions) {
      state.generalInstructions = {
        instructions: '',
        timeframes: [{ threshold: 24 }],
      };
      return;
    }
    //
    // if no custom instructions property, create with empty value
    if (!state.generalInstructions.instructions) {
      state.generalInstructions.instructions = '';
    }
    //
    // if no timeframe property, create with default value
    if (!state.generalInstructions.timeframes?.length) {
      state.generalInstructions.timeframes = [
        {
          enabled: false,
          threshold: 24,
          setDate: new Date(),
        },
      ];
    }
  },
  updateTeamMemberData: (state, memberData) => {
    let memberIndex = state.currentTeam?.members?.findIndex(
      tm => getId(tm.user) === getId(state.userData)
    );
    state.currentTeam?.members?.splice(memberIndex, 0, memberData);
  },
};

//
// actions
const actions = {
  loadTeamDetails: async ({ state, getters, commit }) => {
    try {
      if (state.debateTeams[getters.teamID]) {
        commit('setCurrentTeam', state.debateTeams[getters.teamID]);
      } else {
        const res = await xhr.getTeamDetails(getters.teamID);
        commit('setCurrentTeam', res);
        commit('setTeams', res);
      }
    } catch (err) {
      console.error('error fetching team details');
      console.error(err);
    }
  },
  getTeamList: async (context, { sortColumn, sortDirection, filters }) => {
    try {
      let userTeams = await xhr.getTeamList(sortColumn, sortDirection, filters);
      return userTeams;
    } catch (error) {
      console.error(error);
      // Return empty result set on error (e.g., 404)
      return {
        results: [],
        page: 0,
        resultsPerPage: 0,
        totalCount: 0,
      };
    }
  },
  loadNonMembersData: async ({ getters }) => {
    const selectFields = 'name,activeDocuments,totalDocuments,lastDocument,rating';
    let contactsList = [];
    try {
      let res = await xhr.getUsersData(getters.nonTeamMembers.join(','), selectFields);
      getters.nonTeamMembers.forEach(tm => {
        contactsList.push({
          user: tm,
          ...res.find(r => r._id === tm),
        });
      });
      return contactsList;
    } catch (err) {
      alert(err);
      return null;
    }
  },

  loadGeneralInstructions: async ({ getters, commit }, id) => {
    let teamId = id ? id : getters.teamID;
    const res = await xhr.getDocumentCounterpointInstructions(teamId);
    commit('setGeneralInstructions', res);
  },

  saveGeneralInstructions: async (
    context,
    { teamID, generalInstructions, successFn, failedFn }
  ) => {
    try {
      const res = await xhr.updateDocumentCounterpointInstructions(teamID, generalInstructions);
      if (res && successFn) successFn(res);
    } catch {
      if (failedFn) failedFn();
    }
  },

  updateTeam: async ({ commit }, { teamID, updatedData }) => {
    try {
      const res = await xhr.updateTeam(teamID, updatedData);
      commit('setCurrentTeam', res);
      commit('setTeams', res);
    } catch {
      console.log('error updating team');
    }
  },

  setGeneralInstructionsViewed: async (context, { teamID, successFn, failedFn }) => {
    try {
      const res = await xhr.updateDocumentCounterpointInstructionsViewed(teamID);
      if (successFn) successFn(res);
    } catch {
      if (failedFn) failedFn();
    }
  },
};

//
// exports
export default {
  // namespaced: false,
  state,
  getters,
  actions,
  mutations,
};
